import React from "react";
import {
  Box,
  Flex,
  SimpleGrid,
  Heading,
  Text,
  Icon,
  Link,
  VStack,
  useColorModeValue,
  Container,
  Badge,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import {
  FaLink,
  FaWallet,
  FaTasks,
  FaProjectDiagram,
  FaHandshake,
  FaFolderOpen,
  FaCalendarAlt,
  FaCheckSquare,
  FaSitemap,
  FaHeadphones,
  FaPhotoVideo,
  FaGraduationCap,
  FaMicrophone,
  FaVideo,
  FaExchangeAlt,
} from "react-icons/fa";
import { FEATURES_SECTION } from "../../../content/productGuide";
import { TiltCard } from "../../common/TiltCard";
import { usePublicGlassSurface } from "../../common/publicVisual";

const FEATURES = [
  {
    icon: FaHeadphones,
    title: "Screen AI",
    description: "Share a tab; AI reads screenshots and audio into saved summaries.",
    to: "/dashboard/screen-listen",
    accent: "cyan",
    highlight: true,
  },
  {
    icon: FaPhotoVideo,
    title: "Media AI",
    description: "Upload images or video—AI explains each file or time segment.",
    to: "/dashboard/media-analyze",
    accent: "purple",
    highlight: true,
  },
  {
    icon: FaMicrophone,
    title: "Audio AI",
    description: "Record or upload voice notes—AI transcribes, summarizes, and answers questions.",
    to: "/dashboard/voice-analyse",
    accent: "cyan",
    highlight: true,
  },
  {
    icon: FaVideo,
    title: "Video AI",
    description: "AI builds a timestamped timeline of your videos—ask who or what appears, and when.",
    to: "/dashboard/video-ai",
    accent: "purple",
    highlight: true,
  },
  {
    icon: FaExchangeAlt,
    title: "Transfer AI",
    description: "Post your travel route; others find you by city and date and chat to request a favour.",
    to: "/dashboard/transfer-ai",
    accent: "orange",
    highlight: true,
  },
  {
    icon: FaGraduationCap,
    title: "Exam AI",
    description: "Auto-built syllabus tree plus a fact-checked tutor and current-affairs MCQs.",
    to: "/dashboard/exam-ai",
    accent: "pink",
    highlight: true,
  },
  {
    icon: FaWallet,
    title: "Portfolio AI",
    description: "Track money by folder; optional voice to log and ask totals.",
    to: "/dashboard/portfolio",
    accent: "teal",
    highlight: true,
  },
  {
    icon: FaProjectDiagram,
    title: "Project AI",
    description: "Kanban boards with due dates and voice-friendly task updates.",
    to: "/dashboard/project",
    accent: "blue",
    highlight: true,
  },
  {
    icon: FaCheckSquare,
    title: "Todos AI",
    description: "Nested checklists for today—with optional voice control.",
    to: "/dashboard/todos",
    accent: "green",
    highlight: true,
  },
  {
    icon: FaTasks,
    title: "Daily Chore AI",
    description: "Recurring routines, reminders, and voice for today’s list.",
    to: "/dashboard/daily-chore",
    accent: "purple",
    highlight: true,
  },
  {
    icon: FaLink,
    title: "Notes",
    description: "Save notes and links in folders and find them with search.",
    to: "/dashboard/notes",
    accent: "teal",
  },
  {
    icon: FaFolderOpen,
    title: "Files",
    description: "Store PDFs and other files next to your plans.",
    to: "/dashboard/files",
    accent: "orange",
  },
  {
    icon: FaSitemap,
    title: "Org tree",
    description: "3D org map with todos per node and free local voice navigation.",
    to: "/dashboard/org-tree",
    accent: "teal",
  },
  {
    icon: FaCalendarAlt,
    title: "Calendar",
    description: "Connect Google Calendar for meetings and reminders.",
    to: "/dashboard/calendar-events",
    accent: "pink",
  },
  {
    icon: FaHandshake,
    title: "Split expenses",
    description: "Track who paid what in a group.",
    to: "/dashboard/splitwise",
    accent: "teal",
  },
];

function FeatureCard({ icon, title, description, to, accent, highlight }) {
  const glass = usePublicGlassSurface();
  const muted = useColorModeValue("gray.600", "gray.400");
  const hoverGlow = useColorModeValue(
    "0 16px 48px -28px rgba(45, 212, 191, 0.45)",
    "0 20px 50px -24px rgba(45, 212, 191, 0.25)"
  );

  return (
    <TiltCard
      role="group"
      tilt={8}
      borderWidth={highlight ? "2px" : "1px"}
      borderColor={highlight ? `${accent}.300` : undefined}
      borderRadius="xl"
      p={{ base: 5, md: 6 }}
      h="full"
      display="flex"
      flexDirection="column"
      transition="box-shadow 0.25s ease, border-color 0.25s ease"
      _hover={{ boxShadow: hoverGlow }}
      sx={{ transformStyle: "preserve-3d" }}
      {...glass}
    >
      <VStack align="flex-start" spacing={4} flex="1">
        <Flex
          align="center"
          justify="center"
          w={12}
          h={12}
          borderRadius="xl"
          bg={`${accent}.50`}
          sx={{
            _dark: { bg: "whiteAlpha.100" },
            transform: "translateZ(24px)",
            boxShadow: "0 8px 24px -8px rgba(0,0,0,0.2)",
          }}
        >
          <Icon
            as={icon}
            w={6}
            h={6}
            color={`${accent}.500`}
            sx={{ _dark: { color: `${accent}.300` } }}
          />
        </Flex>
        <Heading as="h3" size="md" fontWeight="bold" lineHeight="short">
          {title}
        </Heading>
        <Text fontSize="sm" color={muted} flex="1">
          {description}
        </Text>
        <Link
          as={RouterLink}
          to={to}
          fontWeight="semibold"
          fontSize="sm"
          color={`${accent}.600`}
          sx={{ _dark: { color: `${accent}.300` } }}
          _hover={{ textDecoration: "none", opacity: 0.85 }}
        >
          Open in app →
        </Link>
      </VStack>
    </TiltCard>
  );
}

const Features = () => {
  const sectionBg = "transparent";
  const blurbColor = useColorModeValue("gray.600", "gray.400");

  return (
    <Box id="features" as="section" py={{ base: 14, md: 20 }} bg={sectionBg}>
      <Container maxW="container.xl" px={{ base: 4, md: 6 }}>
        <VStack spacing={3} textAlign="center" mb={{ base: 10, md: 12 }} maxW="2xl" mx="auto">
          <Badge
            colorScheme="teal"
            borderRadius="full"
            px={3}
            py={1}
            fontSize="xs"
            textTransform="uppercase"
            letterSpacing="wider"
          >
            {FEATURES_SECTION.badge}
          </Badge>
          <Heading as="h2" size="xl" fontWeight="extrabold">
            {FEATURES_SECTION.heading}
          </Heading>
          <Text fontSize="md" color={blurbColor} lineHeight="tall">
            {FEATURES_SECTION.sub}
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={{ base: 5, md: 6 }}>
          {FEATURES.map((f) => (
            <FeatureCard key={f.to} {...f} />
          ))}
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export default Features;
